import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Iteration 90 — checkpoint: admin-automatic-discounts-with-session-targets.
 *
 * Small hook exposing the public state of the Admin-controlled
 * automatic discounts to any customer-facing pricing surface.
 *
 * Returns:
 *   { book: {active, percentage},
 *     sessions: {active, percentage, target_mode, selected_session_ids} }
 *
 * The frontend renders the struck-out original + discounted price using
 * `discountForService` — but the backend ALWAYS re-derives the final
 * amount server-side at /checkout/create so a tampered frontend can
 * never grant an unintended discount.
 */
export default function useAutoDiscounts() {
  const [state, setState] = useState({
    book: { active: false, percentage: 0 },
    sessions: {
      active: false,
      percentage: 0,
      target_mode: null,
      selected_session_ids: [],
    },
  });

  useEffect(() => {
    let alive = true;
    api
      .get("/discounts/active")
      .then(({ data }) => {
        if (!alive || !data) return;
        setState({
          book: {
            active: !!data.book?.active,
            percentage: Number(data.book?.percentage) || 0,
          },
          sessions: {
            active: !!data.sessions?.active,
            percentage: Number(data.sessions?.percentage) || 0,
            target_mode: data.sessions?.target_mode || null,
            selected_session_ids: data.sessions?.selected_session_ids || [],
          },
        });
      })
      .catch(() => {
        // Silent — defaults keep normal pricing behavior.
      });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

/**
 * Given a service (id, type, modality, bundle, price) and the current
 * auto-discount state, return either null or
 * `{ percentage, base, final }` for the struck-out UI.
 *
 * The classifier here MIRRORS the backend one in
 * `backend/core/auto_discounts.py::is_normal_session_id` to keep the UI
 * preview in sync with the authoritative server rule. If the two ever
 * disagree, the server wins (backend re-validates every checkout).
 */
export function discountForService(service, state) {
  if (!service || !state) return null;
  const base = Number(service.price) || 0;
  if (service.type === "book") {
    if (!state.book?.active) return null;
    const pct = Number(state.book.percentage) || 0;
    if (!(pct > 0)) return null;
    const disc = Math.round(base * pct) / 100;
    return { percentage: pct, base, final: Math.round((base - disc) * 100) / 100 };
  }
  const isNormalSession =
    service.type === "consultation" &&
    (service.modality === "voice" || service.modality === "text") &&
    !service.bundle;
  if (!isNormalSession) return null;
  const s = state.sessions;
  if (!s?.active) return null;
  if (s.target_mode === "selected_sessions") {
    if (!s.selected_session_ids?.includes(service.id)) return null;
  }
  const pct = Number(s.percentage) || 0;
  if (!(pct > 0)) return null;
  const disc = Math.round(base * pct) / 100;
  return { percentage: pct, base, final: Math.round((base - disc) * 100) / 100 };
}
